import React, { useEffect, useState } from 'react';
import { func, shape, string } from 'prop-types';
import moment from 'moment';
import { Redirect, withRouter } from 'react-router-dom';
import template from 'url-template';
import { connect, useSelector } from 'react-redux';

import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import EsmsPage from '../../components/EsmsPage';
import lambdaBaseApi from '../../apis/lambda';
import RuleExceptionsTable, {TimestampFilter} from '../../components/RuleExceptionsTable';
import useApi from '../../hooks/useApi';
import useRule from '../../hooks/useRule';
import useTableParams from '../../hooks/useTableParams';

const RuleUrl = template.parse('/rules/{id}/edit');
const RuleExceptionsUrl =
    template.parse('/rules/{id}/executionlogs{?clientId,siteId,entityId,endTimestamp,page,pageSize,query,startTimestamp}');

function parseEndTimestamp(searchParams) {
    const params = new URLSearchParams(searchParams);
    let endDate = moment(params.get('endTimestamp') || undefined).local();
    if (!params.has('endTimestamp')) {
        endDate = moment().local();
    }
    return endDate;
}

function parseStartTimestamp(searchParams) {
    const params = new URLSearchParams(searchParams);
    let startDate = moment(params.get('startTimestamp') || undefined).local();
    if (!params.has('startTimestamp')) {
        startDate = moment().local().subtract(4, 'h');
    }
    return startDate;
}

const RuleDebuggerPage = ({ 
    history, 
    location, 
    match,
    legacyRuleTablePageSize
}) => {
    const ruleId = match.params.id;
    const companyInfo = useSelector(
        ({ companyInfo }) => companyInfo
    );
    const [rule, getRule] = useRule();
    const [redirectTo, setRedirectTo] = useState(null);
    const [startTimestamp, setStartTimestamp] =
        useState(parseStartTimestamp(location.search));
    const [endTimestamp, setEndTimestamp] =
        useState(parseEndTimestamp(location.search));
    const [ruleExceptions, getRuleExceptions] = useApi((params) => ({
        adapter: lambdaBaseApi,
        url:     RuleExceptionsUrl.expand(params)
    }));
    const [workbookModeEnabled, setWorkBookModeEnabled] = useState(false);
    const {
        currentPage,
        onCurrentPageChange,
        onExit,
        onPageSizeChange,
        pageSize,
        pageSizes,
        totalCount
    } = useTableParams(ruleExceptions);

    useEffect(() => {
        getRule(ruleId);

        return () => {
            onExit();
        };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const endTimestamp = parseEndTimestamp(params);
        const startTimestamp = parseStartTimestamp(params);
        setEndTimestamp(endTimestamp);
        setStartTimestamp(startTimestamp);
        if (params.has('page')) {
            onCurrentPageChange(params.get('page'));
        }
        let currentPageSize = pageSize;
        if (params.has('pageSize')) {
            onPageSizeChange(params.get('pageSize'));
            currentPageSize = params.get('pageSize');
        }
        let { page, ...entries } = Object.fromEntries(params.entries());
        if (page) {
            /**
             * Most pagination schemes use 0-based numbering (including the
             * DevExtreme pagination plugin), but the backend is 1-based so we
             * need to adjust the parameter accordingly so that the correct
             * page is requested.
             */
            page = parseInt(page, 10);
        }

        getRuleExceptions({
            ...entries,
            page,
            currentPageSize,
            endTimestamp:   endTimestamp.toISOString(),
            id:             ruleId,
            startTimestamp: startTimestamp.toISOString()
        });

        return () => {
            ruleExceptions.cancel();
        };
    }, [location.search]);

    function getResetQuerystring() {
        const params = new URLSearchParams(location.search);
        params.delete('endTimestamp');
        params.delete('startTimestamp');
        params.delete('page');
        params.delete('pageSize');
        params.delete('orderby');
        return params;
    }

    function handleFilterChange(filterName, value) {
        const newParams = new URLSearchParams(location.search);
        if (value) {
            newParams.set(filterName, value.toISOString());
        } else {
            newParams.delete(filterName);
        }
        newParams.delete('page');
        history.replace({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }

    function linkToRule() {
        const params = getResetQuerystring();
        setRedirectTo({
            pathname: RuleUrl.expand(rule.data),
            search:   params.toString()
        });
    }

    function linkToRules() {
        const params = getResetQuerystring();
        params.set('pageSize', legacyRuleTablePageSize);
        setRedirectTo({
            pathname: '/rules',
            search:   params.toString()
        });
    }

    if (redirectTo) {
        return (
            <Redirect push to={redirectTo} />
        );
    }

    return (
        <EsmsPage
            parents={[
                companyInfo.companyName,
                rule.data.name
            ]}
            title="Rule Debugger"
            handlers={[linkToRules, linkToRule]}
        >
            <div style={{display: 'flex'}}>
                <ActionBar
                    startAdornments={(
                        <TimestampFilter
                            endTimestamp={endTimestamp}
                            onFilterChange={handleFilterChange}
                            startTimestamp={startTimestamp}
                            maxDate={moment().local()}
                            minDate={moment().local().subtract(30, 'd')}
                        />
                    )}
                >
                    <ActionBarItem
                        onClick={() => setWorkBookModeEnabled(!workbookModeEnabled)}
                        type="edit"
                    >
                        Workbook Mode
                    </ActionBarItem>
                </ActionBar>
            </div>
            <RuleExceptionsTable
                currentPage={currentPage}
                error={ruleExceptions.error}
                exceptions={ruleExceptions.data || []}
                isLoading={ruleExceptions.loading}
                onCurrentPageChange={onCurrentPageChange}
                onPageSizeChange={onPageSizeChange}
                pageSize={pageSize}
                pageSizes={pageSizes}
                rule={match.params.id}
                totalCount={totalCount}
                workboookMode={workbookModeEnabled}
                ruleEndTimestamp={endTimestamp}
                ruleStartTimestamp={startTimestamp}
            />
        </EsmsPage>
    );
};

RuleDebuggerPage.propTypes = {
    history: shape({
        replace: func
    }),
    location: shape({
        search: string
    }),
    match: shape({
        params: shape({
            id: string
        })
    })
};

const mapStateToProps = state => ({
    bobLoading:    state.bob.bobLoading,
    kpis:          state.rules.kpis,
    tablePageSize: state.rules.legacyRuleTablePageSize
});

export default withRouter(connect(mapStateToProps, null)(RuleDebuggerPage));
